import type {
  FC,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  RefObject,
} from 'react';
import { createContext, useEffect, useRef, useState } from 'react';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAtom, useAtomValue } from 'jotai';

import type {
  ChatbotPrimeData,
  ChatDialog,
  ChatMessage,
  OpenAiDialogResponse,
} from 'src/types/chatbot';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import {
  chatbotChatAtomFamily,
  chatbotPrimeTexts,
} from 'src/components/chatbot/atoms';
import { preChatDisplayMsg } from 'src/components/chatbot/ChatbotChat/index';
import { CHATBOT_ROLE, USER_ROLE } from 'src/components/chatbot/constants';

interface OpenAiContextValue {
  chatDialog: ChatDialog;
  chatHistoryRef?: RefObject<HTMLElement>;
  error: {
    msg?: string;
    userMsg?: ChatMessage;
    queryParams?: object;
  };
  handleInputSubmit: (
    e?: FormEvent,
    queryParams?: object,
    userDisplayMessage?: string,
  ) => void;
  handleTextAreaKeyDown: (event: ReactKeyboardEvent) => void;
  isLoading: boolean;
  scrollToBottom: () => void;
  setInputValue: (value: string) => void;
  errorHandler: () => void;
}

export const OpenAiContext = createContext<OpenAiContextValue>({
  chatDialog: [],
  chatHistoryRef: undefined,
  error: {},
  handleInputSubmit: () => {},
  handleTextAreaKeyDown: () => {},
  isLoading: false,
  scrollToBottom: () => {},
  setInputValue: () => {},
  errorHandler: () => {},
});

// fallback data
const primeDialogOptions = [
  "What's the situation?",
  'What can I do?',
  'What else is important?',
];

const getPrimeConversation = (primeData: ChatbotPrimeData) =>
  Object.values(primeData).reduce(
    (acc: ChatDialog, { primeText, primeQuestion }, i) => [
      ...acc,
      {
        content:
          primeQuestion || primeDialogOptions[i % primeDialogOptions.length],
        role: USER_ROLE,
        displayMessage:
          primeQuestion || primeDialogOptions[i % primeDialogOptions.length],
      },
      { content: primeText, role: CHATBOT_ROLE, displayMessage: primeText },
    ],
    [],
  );

const getPreChat = (primeData: ChatbotPrimeData) =>
  Object.values(primeData).reduce(
    (acc, { primeText }) => (acc ? `${acc}\n\n${primeText}` : primeText),
    '',
  );

interface OpenAiContextProviderProps {
  chatRoomId: string | number;
  newMessageCallback?: () => void;
  children?: ReactNode;
}

export const OpenAiContextProvider: FC<OpenAiContextProviderProps> = ({
  chatRoomId,
  newMessageCallback,
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [chatDialog, setChatDialog] = useAtom(
    chatbotChatAtomFamily(chatRoomId),
  );

  // there are two ways of priming the chatbot, both can be used at the same time:
  // 1. the preChat that is sent to the chatbot with a text when initiating the chat
  // 2. the fakeChat which is a chat history that has never actually happened
  const preChatTexts = useAtomValue(chatbotPrimeTexts('preChat'));
  const preChat = getPreChat(preChatTexts);
  const fakeChatTexts = useAtomValue(chatbotPrimeTexts('fakeChat'));
  const fakeChat = fakeChatTexts ? getPrimeConversation(fakeChatTexts) : [];

  const chatHistoryRef = useRef<HTMLElement>(null);
  const inputValueRef = useRef('');
  const setInputValue = (value: string) => (inputValueRef.current = value);
  const [error, setError] = useState<{
    msg?: string;
    userMsg?: ChatMessage;
    queryParams?: object;
  }>({});

  useEffect(() => {
    if (chatDialog.length) return;

    if (preChat) {
      inputValueRef.current = preChat;
      handleInputSubmit(undefined, { pre_chat: 1 }, preChatDisplayMsg).finally(
        () => setChatDialog((prevState) => [...prevState, ...fakeChat]),
      );
    } else {
      setChatDialog(fakeChat);
    }
  }, []);

  const scrollToBottom = () => {
    const chatHistory = chatHistoryRef.current;
    if (chatHistory && chatHistory.scrollTo) {
      // use a timeout to set the scroll action at the end of the execution queue
      setTimeout(
        () =>
          chatHistory.scrollTo({
            top: chatHistory.scrollHeight,
            left: 0,
            behavior: 'smooth',
          }),
        0,
      );
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatDialog]);

  const handleInputSubmit = async (
    e?: React.FormEvent,
    queryParams?: object,
    userDisplayMessage?: string,
  ) => {
    e?.preventDefault();
    if (!inputValueRef.current) return;

    setIsLoading(true);
    setError({});
    const newUserMessage: ChatMessage = {
      content: inputValueRef.current,
      role: USER_ROLE,
      displayMessage: userDisplayMessage,
    };
    setChatDialog((prevState) => [...prevState, newUserMessage]);

    try {
      const result: AxiosResponse<OpenAiDialogResponse> =
        await HttpService.post(
          apiPaths.CHATBOT,
          { messages: [...chatDialog, newUserMessage] },
          queryParams,
        );
      const { data } = result;
      if (data?.chatbot_message) {
        newMessageCallback && newMessageCallback();
        setChatDialog((prevState) => [
          ...prevState.slice(0, -1),
          data.user_message
            ? {
                ...data.user_message,
                displayMessage: newUserMessage.displayMessage,
              }
            : newUserMessage,
          {
            ...data.chatbot_message,
            displayMessage: userDisplayMessage && data.chatbot_message.content,
          },
        ]);
        inputValueRef.current = '';
      } else {
        setError({
          msg: 'no chatbot response',
          userMsg: newUserMessage,
          queryParams: queryParams,
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      const errorMsg = axiosError.response?.data?.detail || 'unknown error';
      setError({
        msg: errorMsg,
        userMsg: newUserMessage,
        queryParams: queryParams,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextAreaKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleInputSubmit();
    }
  };

  const errorHandler = () => {
    handleInputSubmit(
      undefined,
      error.queryParams,
      error.userMsg?.displayMessage,
    );
  };

  return (
    <OpenAiContext.Provider
      value={{
        chatDialog,
        chatHistoryRef,
        error,
        handleInputSubmit,
        handleTextAreaKeyDown,
        isLoading,
        scrollToBottom,
        setInputValue,
        errorHandler,
      }}
    >
      {children}
    </OpenAiContext.Provider>
  );
};
